import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Upload, Sparkles, AlertTriangle, UserCheck, UserX } from "lucide-react";
import { toast } from "sonner";

const CRIMSON = "#bf3b4f";
const PRIORITIES = ["Low", "Medium", "High", "Critical"];
const STATUSES = ["New", "In Progress", "Pending", "Waiting to be Deployed", "Resolved"];

/** Paste a backlog list, check what was read, then create them all as issues. */
export function ImportIssuesDialog({ open, onOpenChange, onImported }) {
  const [text, setText] = useState("");
  const [rows, setRows] = useState(null);
  const [unparsed, setUnparsed] = useState("");
  const [busy, setBusy] = useState(false);

  const parse = async () => {
    setBusy(true);
    try {
      const d = await api.issueImportParse({ text, enrich: true });
      setRows(d.rows.map((r) => ({ ...r, include: true })));
      setUnparsed(d.unparsed || "");
      toast.success(`Read ${d.count} issue${d.count === 1 ? "" : "s"}.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not read that list.");
    } finally { setBusy(false); }
  };

  const setRow = (i, patch) => setRows(rows.map((r, x) => (x === i ? { ...r, ...patch } : r)));

  const commit = async () => {
    const chosen = rows.filter((r) => r.include);
    setBusy(true);
    try {
      const d = await api.issueImportCommit({
        rows: chosen.map((r) => ({
          reporter_name: r.reporter_name, matched_email: r.matched_email,
          reported_date: r.reported_date, type: r.type, priority: r.priority,
          status: r.status, body: r.body, ai: r.ai || null,
        })),
      });
      toast.success(`Added ${d.count} issue${d.count === 1 ? "" : "s"} to the tracker.`);
      setText(""); setRows(null); onOpenChange(false); onImported();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not import those issues.");
    } finally { setBusy(false); }
  };

  const chosenCount = (rows || []).filter((r) => r.include).length;
  const unmatched = (rows || []).filter((r) => r.include && !r.matched_email).length;

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) { setRows(null); setText(""); } }}>
      <DialogContent className="max-h-[92vh] flex flex-col sm:max-w-5xl" data-testid="import-dialog">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <Upload className="h-4 w-4" style={{ color: CRIMSON }} />Bulk import issues
          </DialogTitle>
        </DialogHeader>

        {!rows ? (
          <div className="overflow-y-auto space-y-2 pr-1">
            <Label className="text-xs">Paste your list</Label>
            <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={12}
              className="text-xs font-mono" data-testid="import-text"
              placeholder="Paste straight from your spreadsheet — each row needs a date, who reported it, Bug or Feature, a priority, the description and a status." />
            <div className="text-[11px] text-slate-500">
              Rows are read locally, then the AI adds a title, a summary, its own view on priority
              and a prompt for the developer. Nothing is created until you've checked the preview.
            </div>
          </div>
        ) : (
          <div className="overflow-y-auto space-y-2 pr-1" data-testid="import-preview">
            {!!unparsed && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-[11px] text-amber-800 flex items-start gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5 mt-px shrink-0" />
                Some text couldn't be read as an issue: “{unparsed.slice(0, 200)}{unparsed.length > 200 ? "…" : ""}”
              </div>
            )}
            <div className="flex items-center justify-between text-[11px] text-slate-600">
              <span>{chosenCount} of {rows.length} will be imported{unmatched ? ` · ${unmatched} without a matched account` : ""}</span>
              <div className="flex gap-1.5">
                <Button variant="outline" size="sm" className="h-7 text-[10px]" data-testid="import-use-ai"
                  onClick={() => setRows(rows.map((r) => (r.ai_priority ? { ...r, priority: r.ai_priority } : r)))}>
                  <Sparkles className="h-3 w-3 mr-1" />Use all AI priorities
                </Button>
                <Button variant="outline" size="sm" className="h-7 text-[10px]"
                  onClick={() => setRows(rows.map((r) => ({ ...r, priority: r.stated_priority })))}>
                  Use my priorities
                </Button>
              </div>
            </div>

            <div className="space-y-1.5">
              {rows.map((r, i) => (
                <div key={i} className={`rounded-lg border px-2.5 py-2 ${r.include ? "border-slate-200" : "border-slate-100 opacity-50"}`}
                  data-testid={`import-row-${i}`}>
                  <div className="flex items-start gap-2">
                    <Checkbox checked={r.include} onCheckedChange={(v) => setRow(i, { include: !!v })}
                      className="mt-0.5" data-testid={`import-include-${i}`} />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="text-[10px] text-slate-400">{r.date_text}</span>
                        <Badge className={`text-[9px] py-0 border ${r.matched_email ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>
                          {r.matched_email ? <UserCheck className="h-2.5 w-2.5 mr-0.5" /> : <UserX className="h-2.5 w-2.5 mr-0.5" />}
                          {r.reporter_name}
                        </Badge>
                        {r.ai?.title && <span className="text-[11px] font-medium text-slate-800">{r.ai.title}</span>}
                      </div>
                      <div className="text-[11px] text-slate-600 mt-0.5 line-clamp-2">{r.body}</div>
                      {r.ai?.duplicate_of?.length > 0 && (
                        <div className="text-[10px] text-amber-700 mt-0.5">Possible duplicate of {r.ai.duplicate_of.join(", ")}</div>
                      )}
                      <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                        <Select value={r.type} onValueChange={(v) => setRow(i, { type: v })}>
                          <SelectTrigger className="h-6 w-32 text-[10px]" data-testid={`import-type-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Bug" className="text-xs">Bug</SelectItem>
                            <SelectItem value="Feature Request" className="text-xs">Feature Request</SelectItem>
                          </SelectContent>
                        </Select>
                        <Select value={r.priority} onValueChange={(v) => setRow(i, { priority: v })}>
                          <SelectTrigger className="h-6 w-24 text-[10px]" data-testid={`import-priority-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p} className="text-xs">{p}</SelectItem>)}</SelectContent>
                        </Select>
                        <Select value={r.status} onValueChange={(v) => setRow(i, { status: v })}>
                          <SelectTrigger className="h-6 w-44 text-[10px]" data-testid={`import-status-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}</SelectContent>
                        </Select>
                        <span className="text-[10px] text-slate-400">
                          yours: {r.stated_priority}
                          {r.ai_priority && <> · AI: <button className="underline" data-testid={`import-ai-pri-${i}`}
                            onClick={() => setRow(i, { priority: r.ai_priority })}>{r.ai_priority}</button></>}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter className="shrink-0 border-t border-slate-100 pt-3">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          {!rows ? (
            <Button size="sm" style={{ backgroundColor: CRIMSON }} onClick={parse}
              disabled={busy || text.trim().length < 10} data-testid="import-parse">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}Read the list
            </Button>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={() => setRows(null)}>Back to paste</Button>
              <Button size="sm" style={{ backgroundColor: CRIMSON }} onClick={commit}
                disabled={busy || !chosenCount} data-testid="import-commit">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}Import {chosenCount} issues
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
